/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=6976-110540&m=dev
 */

import { useEffect, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Icon } from '@susu/ui/components'

import { buildWardrobeImageUrl } from '../../api/wardrobeApi'
import { ROLE, useCustomerHub } from '../../contexts/CustomerHubContext'
import { useModal } from '../../contexts/ModalContext'
import { useCopyProductCode } from '../../hooks/useCopyProductCode'
import { useWardrobeAlterations } from '../../hooks/useWardrobeAlterations'
import { cn } from '../../lib/utils'
import {
  getWardrobeItemDisplayName,
  getWardrobeItemMaterial,
  getWardrobeItemSize,
} from '../../utils/wardrobe'

import type { EnvironmentWithChina } from '../../App'
import type { GroupedWardrobeItem } from '../../types/wardrobe'

interface WardrobeItemModalProps {
  item: GroupedWardrobeItem
  /**
   * Country the customer is signed in on, from `getResolvedCountryCode`.
   * Decides which of the item's regional sizes is shown.
   */
  countryCode?: string
  /** With `authToken`, lets the modal look up the item's size alterations. */
  env?: EnvironmentWithChina
  authToken?: string
}

function formatPurchaseDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return isoDate
  }
}

interface InfoRowProps {
  label: string
  value: string
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm leading-sm font-medium text-text">{label}</span>
      <span className="text-sm leading-sm font-light text-text">{value}</span>
    </div>
  )
}

export default function WardrobeItemModal({
  item,
  countryCode,
  env,
  authToken,
}: WardrobeItemModalProps) {
  const { t, language } = useI18N()
  const alterationRows = useWardrobeAlterations({
    epc: item.epc,
    env,
    authToken,
    language,
  })
  const [imageError, setImageError] = useState(false)
  const { closeModal, setShowHeader } = useModal()
  const { role } = useCustomerHub()
  const imageUrl = buildWardrobeImageUrl(item.image_uri)
  const isCustomMade = item.type === 'custom_made'
  const displayName = getWardrobeItemDisplayName(item)
  const material = getWardrobeItemMaterial(item)
  // APAC sizing is a customer-facing convenience. In-store staff work against
  // the default reference size in every market, so the regional lookup is gated
  // on the role rather than applied to whoever is looking — withholding the
  // country is what keeps `getWardrobeItemSize` on the default `size` field.
  const sizeCountryCode = role === ROLE.CUSTOMER ? countryCode : undefined
  const size = getWardrobeItemSize(
    item,
    t('wardrobe.customSize'),
    sizeCountryCode,
  )
  const showImage = Boolean(imageUrl) && !imageError
  // Item code + copy icon are in-store only (style advisor), never in the
  // customer-facing My Account wardrobe. `canCopy` is already false for a
  // missing/empty item_code, so no empty row or no-op icon is rendered.
  const { canCopy, productCode, clickableProps } = useCopyProductCode(
    item.item_code,
  )
  const showItemCode = role === ROLE.STYLE_ADVISOR && canCopy

  useEffect(() => {
    setShowHeader(false)
    return () => {
      setShowHeader(true)
    }
  }, [setShowHeader])

  return (
    <div className="h-full flex flex-col">
      {/* Fixed image — does not scroll. Explicit height avoids aspect-ratio flex-column ambiguity. */}
      <div className="relative shrink-0 w-full h-[min(55vh,492px)] bg-background-gray-dark flex items-center justify-center overflow-hidden">
        {showImage ? (
          <img
            src={imageUrl}
            alt={displayName}
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="w-full h-full object-contain p-16"
          />
        ) : (
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>{t('wardrobe.modal.noImage')}</title>
            <rect width="40" height="40" rx="4" fill="#E5E5E5" />
            <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
            <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
          </svg>
        )}

        {/* Close button overlaid on the image */}
        <button
          type="button"
          onClick={closeModal}
          aria-label={t('wardrobe.modal.close')}
          className="absolute top-16 right-16 flex h-32 w-32 items-center justify-center rounded-full bg-background hover:bg-surface-hover transition-[background-color]"
          data-testid="wardrobe-modal-close"
        >
          <Icon name="close" size={16} color="text" />
        </button>

        {isCustomMade && (
          <div className="backdrop-blur-sm absolute top-[6px] left-[6px] flex h-[22px] w-[22px] items-center justify-center rounded-md bg-white-alpha-600">
            <Icon name="custom-made" size={14} />
          </div>
        )}
      </div>

      {/* Scrollable info section */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-16 px-16 pt-16 pb-20">
          {/* Name + material header */}
          <div className="flex flex-col gap-2 pb-20 border-b border-[#efefef]">
            <p className="text-xl leading-xl font-medium text-text">
              {displayName}
            </p>
            {material && material !== displayName && (
              <p className="text-sm leading-sm font-light text-text">
                {material}
              </p>
            )}
            {showItemCode && (
              <p
                className={cn(
                  'flex w-fit items-center gap-4 cursor-pointer',
                  'text-sm leading-sm font-light text-text-secondary',
                )}
                data-testid="wardrobe-item-code"
                {...clickableProps}
              >
                <span>{productCode}</span>
                <span
                  className="shrink-0"
                  data-testid="copy-product-code-icon"
                  aria-hidden="true"
                >
                  <Icon name="copy" size={16} color="secondary" />
                </span>
              </p>
            )}
          </div>

          {/* Info rows */}
          <div className="flex flex-col gap-4">
            {size && <InfoRow label={t('wardrobe.modal.size')} value={size} />}
            <InfoRow
              label={t('wardrobe.modal.ordered')}
              value={formatPurchaseDate(item.purchased_at)}
            />
            <InfoRow
              label={t('wardrobe.modal.quantity')}
              value={String(item.quantity)}
            />
          </div>

          {alterationRows.length > 0 && (
            <div
              className="flex flex-col gap-12 border-t border-[#efefef] pt-20"
              data-testid="wardrobe-alterations"
            >
              <h3 className="text-base leading-base font-medium text-text">
                {t('wardrobe.modal.sizeAlterations')}
              </h3>
              <ul className="flex flex-col gap-8">
                {alterationRows.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center justify-between text-base leading-base font-light text-text"
                    data-testid="wardrobe-alteration-row"
                  >
                    <span>{row.label}</span>
                    <span
                      className={cn(
                        row.direction === 'increase' && 'text-text-success',
                        row.direction === 'decrease' && 'text-text-error',
                      )}
                    >
                      {row.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
